import { KestrelViewerMessageCallbackAPI } from 'features/kestrelViewer/types/KestrelViewerInterfaces'
import { fetchNearbyPhotos } from 'features/kestrelViewer/utils/fetchNearbyPhotos'
import omit from 'lodash/omit'

import { KVMessageDef } from './index'

/**
 * This message is sent when the orbit is placed.
 * It is used to score the 2D photos in how the contribute to the 3D point and set the filtered photos in the context.
 * Expects the message to have the X, Y, and Z location of the orbit
 * @returns KVMessageDef
 */
export function getOrbitPlacedMessage() {
  return {
    type: 'orbitPositionResponse',
    requiredData: ['x', 'y', 'z'],

    callback: async (
      json: Record<string, unknown>,
      kv: KestrelViewerMessageCallbackAPI
    ) => {
      const worldPosition = {
        x: json.x as number,
        y: json.y as number,
        z: json.z as number,
      }

      if (!kv.kestrelModel?.id) {
        console.warn('No kestrel model available for nearby photos API')
        return
      }

      const cameraPoses = kv.kestrelModel.data.cameraPoses
      if (!cameraPoses || cameraPoses.length === 0) {
        console.warn('No camera poses available for nearby photos')
        kv.setFilteredPhotos(null)
        return
      }

      try {
        kv.setFilteringPhotos(true)
        const photoResponses = await fetchNearbyPhotos(
          worldPosition,
          cameraPoses
        )

        const updatedPhotos = photoResponses
          .map((photoResponse) => {
            const existingPhoto = kv.photos.find(
              (photo) => photo.filename === photoResponse.image_name
            )
            if (existingPhoto) {
              return {
                ...existingPhoto,
                projection: {
                  filename: photoResponse.image_name,
                  x: photoResponse.projection.x,
                  y: photoResponse.projection.y,
                  originalImageWidth: photoResponse.width,
                  originalImageHeight: photoResponse.height,
                },
              }
            }
            return null
          })
          .filter((photo): photo is NonNullable<typeof photo> => photo !== null)

        const photosWithoutProjections = kv.photos
          .filter(
            (photo) =>
              !photoResponses.some((pr) => pr.image_name === photo.filename)
          )
          .map((photo) => omit(photo, 'projection'))

        const allPhotos = [...updatedPhotos, ...photosWithoutProjections]
        kv.setPhotos(allPhotos)

        const filteredPhotos = updatedPhotos.length > 0 ? updatedPhotos : null
        kv.setFilteredPhotos(filteredPhotos)
      } catch (error) {
        console.error(
          'Failed to fetch nearby photos for orbit position:',
          error
        )
        kv.setFilteredPhotos(null)
        const clearedPhotos = kv.photos.map((photo) =>
          omit(photo, 'projection')
        )
        kv.setPhotos(clearedPhotos)
      }
      kv.setFilteringPhotos(false)
    },
  } as KVMessageDef
}
